import React from 'react';
import get from 'lodash/get';
import classnames from 'classnames';
import { withStructureContext } from '@Components/Results/structure/StructureContext';
import StructureToolbarZoomComponent from '@Components/Results/structure/StructureToolbarZoom';
import ShoppingCart from '@Components/Results/structure/availability/ShoppingCart';
import StructureSynthesisComponent from '@Components/Results/structure/structure-synthesis.component';
import SubstanceDetails from '@Components/Results/structure/substance-details/SubstanceDetails';
import UserDatabases from '@Services/UserDatabases';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import StructureToolbarOptionsComponentLegacyContext from '@Components/Results/structure/StructureToolbarOptionsContext';
import { TooltipConstants } from '@Components/Tooltip';
import StructureToolbarCustomerIcon from './StructureToolbarCustomerIcon';
import { isCustomerIconVisible } from './structure-utils';
import './structure-toolbar.component.scss';

const TGITEMS_CONTEXT = 'tgitems';

function StructureToolbarComponent(props) {
  const optionsAvailable = () => {
    const { markushRef, markushStr } = get(props, 'structure.details');
    const databaseId = get(props, 'structure.databaseId');
    const structureMixture = get(props, 'structure.mixture');
    const mixture = !!get(props, 'structure.details.mixtureItems');
    const fromDocuments = get(props, 'metadata.fromDocuments');
    const fromRT = get(props, 'metadata.fromRT');
    const fromSynthesisPlanner = get(props, 'metadata.fromSynthesisPlanner');
    const isTargets = get(props, 'metadata.context') === TGITEMS_CONTEXT;
    const isViewPreview = get(props, 'metadata.isViewPreview');
    const canUseStructureAsFilter =
      !!get(props, 'structure.molImage.structure') && !isViewPreview && !fromDocuments && !isTargets && !fromSynthesisPlanner && !fromRT;
    const isMolImagePropertyPresent = !!get(props, 'structure.molImage.path');
    const heatmapMode = !!get(props, 'metadata.heatmapMode');
    const rxn = !!get(props, 'metadata.rxnFile');
    const isReaxysContent = UserDatabases.isDefaultDatabase(databaseId);
    const showMixtureOptions = !structureMixture && !mixture;
    const showViewDetails = mixture && heatmapMode;
    const isSubstances = get(props, 'metadata.context') === 'substances';
    const isReactions = get(props, 'metadata.context') === 'reactions';
    const isStructuredSubstance = !!get(props, 'structure.molImage.structure');

    return (
      (!(mixture || !isMolImagePropertyPresent) && isStructuredSubstance) ||
      isViewPreview ||
      !isSubstances ||
      // eslint-disable-next-line radix
      (!mixture && parseInt(markushRef) > 0 && isReaxysContent) ||
      showViewDetails ||
      (showMixtureOptions && (isStructuredSubstance || isReactions || canUseStructureAsFilter || markushStr || rxn))
    );
  };

  const { availability, supplierNumber, molImage, customerInfo } = get(props, 'structure');
  const fromSynthesisPlanner = get(props, 'metadata.fromSynthesisPlanner');
  const isTargets = get(props, 'metadata.context') === TGITEMS_CONTEXT;
  const mixture = !!get(props, 'structure.details.mixtureItems');
  const showCustomerIcon = isCustomerIconVisible(customerInfo);
  const isMolImagePropertyPresent = molImage?.path;
  const isMolImageStructurePresent = get(molImage, 'structure');
  const heatmapMode = !!get(props, 'metadata.heatmapMode');
  const isDocumentSubstances = !!get(props, 'metadata.fromDocuments');
  const optionsAvailableValue = optionsAvailable();
  const trackingContext = contextFromMetadata(get(props, 'metadata'));
  const tooltipId = TooltipConstants.ids.ResultStructureComponent;
  const xrnId = get(props, 'structure.details.id');
  const showDetails = get(props, 'showDetails') !== false;
  const gridView = !!get(props, 'gridView');

  return (
    <div
      className={classnames('structure-options-bottom', {
        'structure-options-bottom--document-results': isDocumentSubstances,
      })}
      aria-label="Actions for selected substance">
      {showCustomerIcon ? (
        <div className="structure-options-item--wrapper">
          <StructureToolbarCustomerIcon structure={isMolImageStructurePresent} customerInfo={customerInfo} />
        </div>
      ) : null}

      {mixture || !isMolImagePropertyPresent ? null : (
        <div className="structure-options-item--wrapper">
          {availability ? (
            <ShoppingCart
              tooltipId={!fromSynthesisPlanner && tooltipId}
              tooltipPlace={isTargets && 'left'}
              availability={availability}
              structure={get(props, 'structure')}
              trackingContext={trackingContext}
              supplierNumber={supplierNumber}
            />
          ) : null}
        </div>
      )}

      {mixture || !isMolImagePropertyPresent || !xrnId ? null : (
        <div className="structure-options-item--wrapper">
          <StructureSynthesisComponent
            tooltipId={!fromSynthesisPlanner && tooltipId}
            tooltipPlace={isTargets && 'left'}
            trackingContext={trackingContext}
          />
        </div>
      )}

      {mixture || !isMolImagePropertyPresent || !isMolImageStructurePresent ? null : (
        <div className="structure-options-item--wrapper">
          <StructureToolbarZoomComponent
            tooltipId={!fromSynthesisPlanner && tooltipId}
            tooltipPlace={isTargets && 'left'}
            heatmapMode={heatmapMode}
          />
        </div>
      )}

      {showDetails && xrnId && (
        <div className="structure-options-item--wrapper">
          <SubstanceDetails
            tooltipId={!fromSynthesisPlanner && tooltipId}
            tooltipPlace={isTargets ? 'left' : null}
            structure={get(props, 'structure')}
            reactionId={get(props, 'metadata.id')}
            trackingContext={trackingContext}
            metadata={get(props, 'metadata')}
            gridView={gridView}
          />
        </div>
      )}

      {optionsAvailableValue && xrnId && (
        <div className="structure-options-item--wrapper">
          <StructureToolbarOptionsComponentLegacyContext
            tooltipId={!fromSynthesisPlanner && tooltipId}
            tooltipPlace={isTargets && 'left'}
            trackingContext={trackingContext}
          />
        </div>
      )}
    </div>
  );
}

const StructureToolbarComponentWithContext = withStructureContext(StructureToolbarComponent);

export default StructureToolbarComponentWithContext;
